// app/javascript/controllers/special/gmenu_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["menu", "backdrop"]

  connect() {
    this.handleEscape = this.handleEscape.bind(this)
    this.ensureMenuHidden = this.ensureMenuHidden.bind(this)

    // Состояние контроллера
    this.openTimeout = null
    this.closeTimeout = null
    this.isManuallyOpened = false  // открыто через клик
    this.isHovering = false        // сейчас наводим мышь
    this.lastAction = null         // последнее действие для дебага

    // Turbo-обработчики
    document.addEventListener("turbo:load", this.ensureMenuHidden)
    document.addEventListener("turbo:before-cache", this.ensureMenuHidden)
    document.addEventListener("turbo:before-render", this.ensureMenuHidden)
    document.addEventListener("keydown", this.handleEscape)

    // Для отладки - открываем меню сразу при загрузке
    // this.open()

    console.log("GmenuController connected")
  }

  disconnect() {
    this.clearAllTimeouts()
    document.removeEventListener("turbo:load", this.ensureMenuHidden)
    document.removeEventListener("turbo:before-cache", this.ensureMenuHidden)
    document.removeEventListener("turbo:before-render", this.ensureMenuHidden)
    document.removeEventListener("keydown", this.handleEscape)

    // Включаем скролл при отключении контроллера
    this.enableScroll()

  }

  clearAllTimeouts() {
    if (this.openTimeout) {
      clearTimeout(this.openTimeout)
      this.openTimeout = null
    }
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout)
      this.closeTimeout = null
    }
  }

  // Прячет меню при turbo-переходах
  ensureMenuHidden() {
    if (this.hasMenuTarget) {
      this.clearAllTimeouts()
      this.resetState()
      this.close()
      this.enableScroll()
      // this.open()
    }
  }

  resetState() {
    this.isManuallyOpened = false
    this.isHovering = false
    this.lastAction = 'reset'

  }

  // Проверяет, открыто ли меню
  get isOpen() {
    return this.hasMenuTarget &&
      !this.menuTarget.classList.contains("-translate-x-full")
  }

  // Click toggle
  toggle(event) {
    event?.preventDefault()
    event?.stopPropagation()

    this.clearAllTimeouts()
    this.lastAction = 'click'

    if (this.isOpen) {
      this.isManuallyOpened = false
      this.close()
      console.log("Menu closed by click")
    } else {
      this.isManuallyOpened = true
      this.open()
      console.log("Menu opened by click")
    }
  }

  open() {
    if (!this.hasMenuTarget || this.isOpen) return

    // Блокируем скролл body
    this.disableScroll()

    // Анимация backdrop
    if (this.hasBackdropTarget) {
      this.backdropTarget.classList.remove("opacity-0", "pointer-events-none")
      this.backdropTarget.classList.add("opacity-100", "pointer-events-auto")
    }

    // Анимация меню
    requestAnimationFrame(() => {
      this.menuTarget.classList.remove("-translate-x-full", "opacity-0")
      this.menuTarget.classList.add("-translate-x-0", "opacity-100")
    })

    console.log(`Menu opened (${this.lastAction})`)
  }

  close() {
    if (!this.hasMenuTarget) return

    // Включаем скролл body
    this.enableScroll()

    // Анимация backdrop
    if (this.hasBackdropTarget) {
      this.backdropTarget.classList.remove("opacity-100", "pointer-events-auto")
      this.backdropTarget.classList.add("opacity-0", "pointer-events-none")
    }

    // Анимация меню
    this.menuTarget.classList.remove("-translate-x-0", "opacity-100")
    this.menuTarget.classList.add("-translate-x-full", "opacity-0")

    console.log(`Menu closed (${this.lastAction})`)
  }

  // Hover открытие с задержкой
  hoverOpen(event) {
    // Игнорируем, если это touch-событие на мобильных
    if (this.isTouchDevice()) return

    this.isHovering = true
    this.lastAction = 'hover-open'

    // Если меню открыто вручную - не трогаем
    if (this.isManuallyOpened) {
      console.log("Hover open ignored - manually opened")
      return
    }

    // Отменяем закрытие, если планировалось
    this.clearCloseTimeout()

    // Если уже есть таймаут на открытие - не создаем новый
    if (this.openTimeout) {
      console.log("Hover open ignored - already pending")
      return
    }

    console.log("Hover open scheduled")
    this.openTimeout = setTimeout(() => {
      // Проверяем, что мышь все еще наведена
      if (this.isHovering && !this.isManuallyOpened) {
        this.open()
      }
      this.openTimeout = null
    }, 300)
  }

  // Hover закрытие с задержкой
  hoverClose(event) {
    // Игнорируем, если это touch-событие на мобильных
    if (this.isTouchDevice()) return

    this.isHovering = false
    this.lastAction = 'hover-close'

    // Если меню открыто вручную - не закрываем через hover
    if (this.isManuallyOpened) {
      console.log("Hover close ignored - manually opened")
      return
    }

    // Отменяем открытие, если планировалось
    this.clearOpenTimeout()

    // Если уже есть таймаут на закрытие - не создаем новый
    if (this.closeTimeout) {
      console.log("Hover close ignored - already pending")
      return
    }

    console.log("Hover close scheduled")
    this.closeTimeout = setTimeout(() => {
      // Проверяем, что мышь все еще не наведена и не открыто вручную
      if (!this.isHovering && !this.isManuallyOpened && this.isOpen) {
        this.close()
      }
      this.closeTimeout = null
    }, 300)
  }

  // Клик по backdrop (затемненная область)
  backgroundClick(event) {
    // Убедимся, что это именно backdrop
    if (!event.target.hasAttribute('data-gmenu-target') ||
      event.target.getAttribute('data-gmenu-target') !== 'backdrop') {
      return
    }

    event.preventDefault()
    event.stopPropagation()

    this.clearAllTimeouts()
    this.resetState()
    this.close()
    this.lastAction = 'backdrop-click'

    console.log("Menu closed by backdrop click")
  }

  // Обработка клавиши Esc
  handleEscape(event) {
    if (event.key === "Escape" && this.isOpen) {
      event.preventDefault()
      this.clearAllTimeouts()
      this.resetState()
      this.close()
      this.lastAction = 'escape'
      console.log("Menu closed by Escape")
    }
  }

  // Блокировка скролла
  disableScroll() {
    // Сохраняем текущую позицию скролла
    this.scrollPosition = window.pageYOffset || document.documentElement.scrollTop

    document.body.style.overflow = 'hidden'
    document.body.style.position = 'fixed'
    document.body.style.top = `-${this.scrollPosition}px`
    document.body.style.width = '100%'
  }

  // Восстановление скролла
  enableScroll() {
    document.body.style.overflow = ''
    document.body.style.position = ''
    document.body.style.top = ''
    document.body.style.width = ''

    // Восстанавливаем позицию скролла
    if (this.scrollPosition !== undefined) {
      window.scrollTo(0, this.scrollPosition)
      this.scrollPosition = undefined
    }
  }

  // Вспомогательные методы
  clearOpenTimeout() {
    if (this.openTimeout) {
      clearTimeout(this.openTimeout)
      this.openTimeout = null
    }
  }

  clearCloseTimeout() {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout)
      this.closeTimeout = null
    }
  }

  // Определение touch-устройства
  isTouchDevice() {
    return (('ontouchstart' in window) ||
      (navigator.maxTouchPoints > 0) ||
      (navigator.msMaxTouchPoints > 0))
  }

  // Дебаг-информация (можно удалить в продакшене)
  logState() {
    console.log({
      isOpen: this.isOpen,
      isManuallyOpened: this.isManuallyOpened,
      isHovering: this.isHovering,
      lastAction: this.lastAction,
      hasOpenTimeout: !!this.openTimeout,
      hasCloseTimeout: !!this.closeTimeout
    })
  }
}